import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  
  Button,
  useToast,
  Icon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Flex,
  Avatar,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spacer,
  Divider,
  Portal} from '@chakra-ui/react';
import { 
  FiChevronRight, 
  FiUsers, 
  FiMail, 
  FiPhone, 
  FiCalendar, 
  FiArrowLeft, 
  FiSearch, 
  FiDownload, 
  FiRefreshCw,
  FiMoreVertical,
  FiFileText,
  FiClock,
  FiTrendingUp,
  FiBook,
  FiCopy
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import Layout from '../shared/Layout';

const BacReviewRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const secondaryBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const glassBg = useColorModeValue('whiteAlpha.800', 'blackAlpha.700');
  const accentGradient = 'linear(to-r, blue.400, purple.500)';
  const avatarBg = useColorModeValue('gray.200', 'gray.700');
  const avatarColor = useColorModeValue('gray.700', 'white');

  const fetchRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/users/bac-review-registrations');
      if (response.data.success) {
        setRegistrations(response.data.data);
        setFilteredRegistrations(response.data.data);
      }
    } catch (error) {
      toast({
        title: "خطأ",
        description: "فشل في جلب قائمة التسجيلات",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  useEffect(() => {
    const filtered = registrations.filter(reg => 
      reg.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reg.phoneNumber && reg.phoneNumber.includes(searchTerm))
    );
    setFilteredRegistrations(filtered);
  }, [searchTerm, registrations]);

  const exportToCSV = () => {
    if (filteredRegistrations.length === 0) {
      toast({
        title: "تنبيه",
        description: "لا توجد بيانات لتصديرها",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const headers = ['اسم التلميذ', 'البريد الإلكتروني', 'رقم الهاتف', 'المواد', 'تاريخ التسجيل'];
    const csvContent = [
      headers.join(','),
      ...filteredRegistrations.map(reg => {
        const date = new Date(reg.createdAt).toLocaleDateString('fr-FR');
        const matiers = reg.matiers ? reg.matiers.join(' - ') : '';
        return `"${reg.username}","${reg.email}","${reg.phoneNumber || ''}","${matiers}","${date}"`;
      })
    ].join('\n');

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `تلفونات_تسجيل_مراجعة_البكالوريا.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "تم التصدير بنجاح",
      description: "تم تحميل ملف Excel الخاص بالتسجيلات",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "تم النسخ",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const stats = [
    { 
      label: 'إجمالي المسجلين', 
      value: registrations.length, 
      icon: FiUsers, 
      color: 'blue.500',
      bg: 'blue.50'
    },
    { 
      label: 'تسجيلات اليوم', 
      value: registrations.filter(r => new Date(r.createdAt).toDateString() === new Date().toDateString()).length, 
      icon: FiTrendingUp, 
      color: 'green.500',
      bg: 'green.50'
    },
    { 
      label: 'المواد المستهدفة', 
      value: '20 حصة', 
      icon: FiBook, 
      color: 'purple.500',
      bg: 'purple.50'
    },
    { 
      label: 'الموعد النهائي', 
      value: '05/06', 
      icon: FiClock, 
      color: 'orange.500',
      bg: 'orange.50'
    }
  ];

  return (
    <Layout showHeader={false} showFooter={false}>
      <Box 
        minH="100vh" 
        bg={useColorModeValue('gray.50', 'gray.900')}
        bgImage="radial-gradient(circle at 2px 2px, rgba(0,0,0,0.05) 1px, transparent 0)"
        bgSize="40px 40px"
      >
        <Container maxW="8xl" py={8}>
          <VStack spacing={8} align="stretch">
            {/* Top Navigation & Actions */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
              <VStack align="start" spacing={1}>
                <Breadcrumb 
                  spacing="8px" 
                  separator={<FiChevronRight color="gray.500" />}
                  fontSize="sm"
                  color="gray.500"
                >
                  <BreadcrumbItem>
                    <BreadcrumbLink onClick={() => navigate('/admin/dashboard')}>لوحة التحكم</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink color="blue.500" fontWeight="semibold">تسجيلات مراجعة البكالوريا</BreadcrumbLink>
                  </BreadcrumbItem>
                </Breadcrumb>
                <Heading size="xl" bgGradient={accentGradient} bgClip="text" py={2}>
                  إدارة تسجيلات البكالوريا
                </Heading>
                <Text color="gray.500" fontWeight="medium">
                  {registrations.length} تلميذ مسجل في برنامج المراجعة الشاملة
                </Text>
              </VStack>

              <HStack spacing={3}>
                <Button
                  leftIcon={<FiDownload />}
                  variant="solid"
                  bgGradient={accentGradient}
                  color="white"
                  _hover={{ opacity: 0.9, transform: 'translateY(-1px)' }}
                  _active={{ transform: 'translateY(1px)' }}
                  borderRadius="xl"
                  boxShadow="md"
                  onClick={exportToCSV}
                  isLoading={loading}
                >
                  تصدير CSV
                </Button>
                <IconButton
                  aria-label="العودة"
                  icon={<FiArrowLeft />}
                  onClick={() => navigate('/admin/dashboard')}
                  variant="outline"
                  bg="white"
                  _dark={{ bg: 'gray.800' }}
                  colorScheme="gray"
                  borderRadius="xl"
                  boxShadow="sm"
                />
              </HStack>
            </Flex>

            {/* Stats Overview */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              {stats.map((stat, idx) => (
                <Card 
                  key={idx} 
                  borderRadius="2xl" 
                  border="1px solid" 
                  borderColor={borderColor}
                  boxShadow="sm"
                  overflow="hidden"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
                >
                  <CardBody>
                    <Flex align="center">
                      <Box 
                        p={3} 
                        bg={stat.bg} 
                        _dark={{ bg: 'whiteAlpha.100' }}
                        borderRadius="xl" 
                        color={stat.color}
                        mr={4}
                      >
                        <Icon as={stat.icon} boxSize={6} />
                      </Box>
                      <Stat>
                        <StatLabel color="gray.500" fontWeight="bold">{stat.label}</StatLabel>
                        <StatNumber fontSize="2xl">{stat.value}</StatNumber>
                      </Stat>
                    </Flex>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>

            {/* Search and Filters */}
            <Card 
              borderRadius="2xl" 
              bg={glassBg} 
              backdropFilter="blur(10px)"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="md"
            >
              <CardBody p={4}>
                <HStack spacing={4} wrap="wrap">
                  <InputGroup maxW={{ base: 'full', md: '400px' }} size="lg">
                    <InputLeftElement pointerEvents="none">
                      <FiSearch color="gray.600" />
                    </InputLeftElement>
                    <Input
                      placeholder="بحث بالاسم، البريد أو الهاتف..."
                      borderRadius="xl"
                      bg={secondaryBg}
                      border="none"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      _focus={{ boxShadow: 'outline', bg: bgColor }}
                    />
                  </InputGroup>
                  <Spacer />
                  <Button 
                    rightIcon={<FiRefreshCw />} 
                    variant="ghost" 
                    colorScheme="blue"
                    borderRadius="xl"
                    onClick={fetchRegistrations}
                    isLoading={loading}
                    display={{ base: 'none', md: 'flex' }}
                  >
                    تحديث البيانات
                  </Button>
                </HStack>
              </CardBody>
            </Card>

            {/* Registrations Table */}
            <Card 
              borderRadius="2xl" 
              overflow="hidden" 
              border="1px solid" 
              borderColor={borderColor}
              boxShadow="xl"
              bg={bgColor}
            >
              <Box overflowX="auto">
                <Table variant="simple" size="lg">
                  <Thead bg={secondaryBg}>
                    <Tr>
                      <Th fontSize="sm" py={5}>التلميذ</Th>
                      <Th fontSize="sm" py={5}>معلومات الاتصال</Th>
                      <Th fontSize="sm" py={5}>المواد المسجلة</Th>
                      <Th fontSize="sm" py={5}>تاريخ التسجيل</Th>
                      <Th fontSize="sm" py={5} textAlign="center"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {loading ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" py={20}>
                          <CobraSpinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
                          <Text mt={4} color="gray.500" fontWeight="medium">جاري تحميل البيانات...</Text>
                        </Td>
                      </Tr>
                    ) : filteredRegistrations.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" py={20}>
                          <VStack spacing={4}>
                            <Icon as={FiFileText} boxSize={12} color="gray.700" />
                            <Text color="gray.500" fontSize="lg">لا توجد داتا مطابقة לבحثك</Text>
                          </VStack>
                        </Td>
                      </Tr>
                    ) : (
                      filteredRegistrations.map((reg) => (
                        <Tr 
                          key={reg._id} 
                          _hover={{ bg: 'gray.50', _dark: { bg: 'whiteAlpha.50' } }} 
                          transition="all 0.2s"
                        >
                          <Td>
                            <HStack spacing={4}>
                              <Avatar 
                                size="md" 
                                name={reg.username} 
                                bg={avatarBg}
                                color={avatarColor}
                                fontWeight="bold"
                              />
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="bold" fontSize="md">{reg.username}</Text>
                                <Badge colorScheme="blue" variant="subtle" borderRadius="full">طالب</Badge>
                              </VStack>
                            </HStack>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={1}>
                              <HStack spacing={2}>
                                <Icon as={FiMail} color="gray.600" />
                                <Text fontSize="sm">{reg.email}</Text>
                              </HStack>
                              <HStack spacing={2}>
                                <Icon as={FiPhone} color="green.500" />
                                <Text fontSize="sm" fontWeight="semibold" dir="ltr">{reg.phoneNumber || '---'}</Text>
                              </HStack>
                            </VStack>
                          </Td>
                          <Td>
                            <Flex wrap="wrap" gap={2}>
                              {reg.matiers?.map((m, idx) => (
                                <Badge 
                                  key={idx} 
                                  colorScheme="purple" 
                                  variant="solid" 
                                  borderRadius="lg" 
                                  px={2} 
                                  py={0.5}
                                  fontSize="2xs"
                                >
                                  {m}
                                </Badge>
                              ))}
                            </Flex>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Icon as={FiCalendar} color="blue.500" />
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" fontWeight="bold">
                                  {new Date(reg.createdAt).toLocaleDateString('en-GB')}
                                </Text>
                                <Text fontSize="2xs" color="gray.500">
                                  {new Date(reg.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </Text>
                              </VStack>
                            </HStack>
                          </Td>
                          <Td textAlign="center">
                            <Menu isLazy>
                              <MenuButton 
                                as={IconButton} 
                                icon={<FiMoreVertical />} 
                                variant="ghost" 
                                colorScheme="gray"
                                borderRadius="full"
                              />
                              <Portal>
                                <MenuList borderRadius="xl" boxShadow="xl" p={2}>
                                  <MenuItem 
                                    icon={<FiMail />} 
                                    borderRadius="lg"
                                    onClick={() => window.open(`mailto:${reg.email}`)}
                                  >
                                    مراسلة البريد
                                  </MenuItem>
                                  <MenuItem 
                                    icon={<FiPhone />} 
                                    borderRadius="lg"
                                    onClick={() => window.open(`tel:${reg.phoneNumber}`)}
                                    isDisabled={!reg.phoneNumber}
                                  >
                                    اتصال بالهاتف
                                  </MenuItem>
                                  <Divider my={2} />
                                  <MenuItem 
                                    icon={<FiCopy />} 
                                    color="blue.500" 
                                    borderRadius="lg"
                                    onClick={() => copyToClipboard(`${reg.username} - ${reg.phoneNumber || ''}`)}
                                  >
                                    نسخ المعلومات
                                  </MenuItem>
                                </MenuList>
                              </Portal>
                            </Menu>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Card>
          </VStack>
        </Container>
      </Box>
    </Layout>
  );
};

export default BacReviewRegistrations;

